import * as React from 'react';
import { importAbsences, ImportAbsencesPreviewRow, downloadAbsencesTemplate, downloadAbsencesTemplateLocalFallback, TemplateOpts } from '../api';
import { colors, shadows } from '../../employees/styles/personalakteStyles';

type Props = {
  open: boolean;
  onClose: () => void;
  onCommitted?: () => Promise<void> | void; // trigger reloads after commit
  periodId?: number | null; // optional context
};

export default function ImportAbsencesModal({ open, onClose, onCommitted }: Props): React.JSX.Element | null {
  // Lazy ExcelJS import (identisch zum TT-Upload-Pattern)
  let __exceljsPromise: Promise<typeof import('exceljs')> | null = (ImportAbsencesModal as any).__exceljsPromise || null;
  function getExcelJS() {
    if (!__exceljsPromise) __exceljsPromise = import('exceljs');
    (ImportAbsencesModal as any).__exceljsPromise = __exceljsPromise;
    return __exceljsPromise;
  }

  const [file, setFile] = React.useState<File | null>(null);
  const [jsonText, setJsonText] = React.useState('');
  const [preview, setPreview] = React.useState<ImportAbsencesPreviewRow[]>([]);
  const [dryRun, setDryRun] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [summary, setSummary] = React.useState<{ errors: number; creates?: number; updates?: number; skips?: number } | null>(null);

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setJsonText('');
      setPreview([]);
      setDryRun(true);
      setBusy(false);
      setError('');
      setSummary(null);
    }
  }, [open]);

  const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 };
  const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)', padding: '24px', width: 740, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', animation: 'fadeIn 0.15s ease-out' };
  const btnPrimary: React.CSSProperties = { background: colors.navy, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s' };
  const btnSecondary: React.CSSProperties = { background: '#fff', color: colors.navy, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.15s' };
  const btnDanger: React.CSSProperties = { background: '#fff', color: colors.danger, border: `1px solid ${colors.dangerLight}`, borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.15s' };
  const inputStyle: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13, outline: 'none', transition: 'border-color 0.15s' };
  const selectStyle: React.CSSProperties = { ...inputStyle, background: '#fff' };
  const sectionStyle: React.CSSProperties = { padding: 16, border: `1px solid ${colors.border}`, borderRadius: 12, background: colors.bgMuted };
  const labelStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 };
  const labelTextStyle: React.CSSProperties = { color: colors.textBody, fontWeight: 500 };
  const sectionHeaderStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 };

  const [tplFormat, setTplFormat] = React.useState<'xlsx'|'csv'>('xlsx');
  const [tplPrefill, setTplPrefill] = React.useState<'none'|'all'|'active'>('none');
  const [tplWA, setTplWA] = React.useState<string>('');
  const [tplGroup, setTplGroup] = React.useState<string>('');
  const [tplSelected, setTplSelected] = React.useState<boolean>(false);
  const [tplSelectedIdsText, setTplSelectedIdsText] = React.useState<string>('');
  const [tplExamples, setTplExamples] = React.useState<boolean>(true);
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);
  const [mergeMode, setMergeMode] = React.useState<'auto'|'replace'|'none'>('auto');

  async function handleDownloadTemplate() {
    const prefill = tplSelected ? 'selected' : tplPrefill;
    const selectedIds = tplSelectedIdsText
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const normIds = selectedIds.map((s) => (/^\d+$/.test(s) ? Number(s) : s));
    const opts: TemplateOpts = {
      format: tplFormat,
      prefill: prefill as any,
      work_area: tplWA || undefined,
      payroll_group: tplGroup || undefined,
      employee_ids: prefill === 'selected' ? normIds : undefined,
      examples: tplExamples,
      delimiter: tplFormat === 'csv' ? 'semicolon' : undefined,
    };
    try {
      await downloadAbsencesTemplate(opts);
    } catch (e: any) {
      setError('Template-Endpoint (Backend) nicht verfügbar. Lokaler Fallback wird angeboten.');
      await downloadAbsencesTemplateLocalFallback(tplFormat);
    }
  }

  if (!open) return null;

  async function handlePreview() {
    setError('');
    try {
      setBusy(true);
      const parsed = file ? await parseImportFileToRows(file) : parseJsonRows(jsonText);
      const allRows = Array.isArray((parsed as any)?.rows) ? (parsed as any).rows : [];
      if (process.env.NODE_ENV !== 'production') {
        try {
          // eslint-disable-next-line no-console
        } catch {}
      }
      const toBool = (v: any) => typeof v === 'boolean' ? v : ['true','1','ja','wahr','yes','y','t'].includes(String(v||'').trim().toLowerCase());
      const localSkip = allRows.filter((r: any) => toBool((r as any)._skip));
      const realRows = allRows.filter((r: any) => !toBool((r as any)._skip));

      const res = await importAbsences({ rows: realRows, merge: mergeMode } as any, true);
      const serverRows: ImportAbsencesPreviewRow[] = Array.isArray((res as any)?.rows) ? (res as any).rows : (Array.isArray(res) ? (res as any) : []);

      const demoPreviewRows: ImportAbsencesPreviewRow[] = localSkip.map((r: any) => ({
        action: 'skip',
        employee_name: r.employee_name,
        employee: r.employee_id || r.employee,
        employee_email: r.employee_email,
        datev_personalnr: r.datev_personalnr,
        absence_type: r.absence_type || r.type,
        start_datetime: r.start_datetime || r.from,
        end_datetime: r.end_datetime || r.to,
      }));

      const rows: ImportAbsencesPreviewRow[] = [...demoPreviewRows, ...serverRows];
      setPreview(rows);
      const sum = (res as any)?.summary || {};
      const inferredErrors = rows.filter(r => String(r.action || '').toLowerCase() === 'error').length;
      const baseSkips = typeof sum.skips === 'number' ? sum.skips : 0;
      setSummary({
        errors: typeof sum.errors === 'number' ? sum.errors : inferredErrors,
        creates: typeof sum.creates === 'number' ? sum.creates : undefined,
        updates: typeof sum.updates === 'number' ? sum.updates : undefined,
        skips: baseSkips + demoPreviewRows.length,
      });
      setDryRun(true);
    } catch (e: any) {
      const status = e?.status;
      const data = e?.data;
      const parsed = file ? await parseImportFileToRows(file) : parseJsonRows(jsonText);
      const allRows = Array.isArray((parsed as any)?.rows) ? (parsed as any).rows : [];
      if (process.env.NODE_ENV !== 'production') {
        try {
          // eslint-disable-next-line no-console
        } catch {}
      }
      const toBool = (v: any) => typeof v === 'boolean' ? v : ['true','1','ja','wahr','yes','y','t'].includes(String(v||'').trim().toLowerCase());
      const localSkip = allRows.filter((r: any) => toBool((r as any)._skip));
      const demoPreviewRows: ImportAbsencesPreviewRow[] = localSkip.map((r: any) => ({
        action: 'skip',
        employee_name: r.employee_name,
        employee: r.employee_id || r.employee,
        employee_email: r.employee_email,
        datev_personalnr: r.datev_personalnr,
        absence_type: r.absence_type || r.type,
        start_datetime: r.start_datetime || r.from,
        end_datetime: r.end_datetime || r.to,
      }));
      const rows: ImportAbsencesPreviewRow[] = [...demoPreviewRows, ...(Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : []))];
      if (rows.length > 0) {
        setPreview(rows);
        const sum = (data as any)?.summary || {};
        const inferredErrors = rows.filter(r => String(r.action || '').toLowerCase() === 'error').length;
        const baseSkips = typeof sum.skips === 'number' ? sum.skips : 0;
        setSummary({
          errors: typeof sum.errors === 'number' ? sum.errors : inferredErrors,
          creates: typeof sum.creates === 'number' ? sum.creates : undefined,
          updates: typeof sum.updates === 'number' ? sum.updates : undefined,
          skips: baseSkips + demoPreviewRows.length,
        });
      }
      const detail = e?.data?.detail || e?.message;
      if (status === 401 || status === 403) setError('Bitte neu einloggen / Berechtigung prüfen.');
      else if (!rows.length) setError(detail || 'Preview fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    setError('');
    try {
      setBusy(true);
      const parsed = file ? await parseImportFileToRows(file) : parseJsonRows(jsonText);
      const allRows = Array.isArray((parsed as any)?.rows) ? (parsed as any).rows : [];
      const toBool = (v: any) => typeof v === 'boolean' ? v : ['true','1','ja','wahr','yes','y','t'].includes(String(v||'').trim().toLowerCase());
      const realRows = allRows.filter((r: any) => !toBool((r as any)._skip));
      await importAbsences({ rows: realRows, merge: mergeMode } as any, false);
      if (onCommitted) await onCommitted();
      onClose();
    } catch (e: any) {
      const status = e?.status;
      const detail = e?.data?.detail || e?.message;
      if (status === 401 || status === 403) setError('Bitte neu einloggen / Berechtigung prüfen.');
      else setError(detail || 'Import fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  function parseJsonRows(text: string): { rows: any[] } {
    try {
      const obj = JSON.parse(text || '[]');
      if (Array.isArray(obj)) return { rows: obj };
      if (obj && Array.isArray((obj as any).rows)) return { rows: (obj as any).rows };
      return { rows: [] };
    } catch {
      return { rows: [] };
    }
  }

  async function parseImportFileToRows(file: File): Promise<{ rows: any[] }> {
    const ext = (file.name || '').toLowerCase();
    const isCsv = ext.endsWith('.csv');
    const isXlsx = ext.endsWith('.xlsx') || ext.endsWith('.xls');

    if (isCsv) {
      const text = await file.text();
      const ExcelJS = await getExcelJS();
      const wb = new ExcelJS.Workbook();
      const stream = new ReadableStream({ start(c) { c.enqueue(new TextEncoder().encode(text)); c.close(); } });
      await wb.csv.read(stream as any);
      return worksheetToRowsExcel(wb);
    }
    if (isXlsx) {
      const buf = await file.arrayBuffer();
      const ExcelJS = await getExcelJS();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buf);
      return worksheetToRowsExcel(wb);
    }

    const json = JSON.parse(await file.text());
    if (Array.isArray(json)) return { rows: json };
    if (json && Array.isArray((json as any).rows)) return { rows: (json as any).rows };
    return { rows: [] };
  }

  function worksheetToRowsExcel(wb: any): { rows: any[] } {
    const ws = wb.worksheets?.[0];
    if (!ws) return { rows: [] };
    const aoa: any[][] = [];
    ws.eachRow({ includeEmpty: true }, (row: any) => {
      aoa.push((row.values as any[]).slice(1));
    });
    if (aoa.length === 0) return { rows: [] };
    const headers = aoa[0].map((h: any) => String(h ?? ''));
    const raw = aoa.slice(1).map(rowArr => {
      const obj: Record<string, any> = {};
      headers.forEach((h, i) => { obj[h] = rowArr[i] ?? ''; });
      return obj;
    });
    const rows = raw
      .map(normalizeAbsenceRow)
      .filter(r => Object.keys(r).length > 0);
    return { rows };
  }

  const HMAP: Record<string, string> = {
    'employee_id': 'employee_id',
    'id': 'employee_id',
    'employee': 'employee_id',
    'employee_email': 'employee_email',
    'email': 'employee_email',
    'datev_personalnr': 'datev_personalnr',
    'personalnr': 'datev_personalnr',
    'datev': 'datev_personalnr',
    'type': 'type',
    'absence_type': 'type',
    'art': 'type',
    'from': 'from',
    'start': 'from',
    'start_date': 'from',
    'von': 'from',
    'to': 'to',
    'end': 'to',
    'end_date': 'to',
    'bis': 'to',
    'status': 'status',
    'full_day': 'full_day',
    'fullday': 'full_day',
    'full day': 'full_day',
    'ganztag': 'full_day',
    'note': 'note',
    'comment': 'note',
    '_skip': '_skip',
    '__skip': '_skip',
    'skip': '_skip',
  };

  function normalizeAbsenceRow(raw: Record<string, any>): any {
    const norm: Record<string, any> = {};
    for (const [k, v] of Object.entries(raw)) {
      let key = String(k).trim().toLowerCase();
      if (key.charCodeAt(0) === 0xfeff) key = key.slice(1);
      const tgt = HMAP[key];
      if (tgt) norm[tgt] = v;
    }

    if (!norm.employee_id && !norm.employee_email && !norm.datev_personalnr) {
      return norm;
    }

    if (norm.type) norm.type = normalizeType(norm.type);
    if (!norm.status) norm.status = 'approved';
    else norm.status = normalizeStatus(norm.status);

    if (norm.from) norm.from = toISODate(norm.from);
    if (norm.to) norm.to = toISODate(norm.to);

    if (typeof norm.full_day === 'string') {
      const s = norm.full_day.trim().toLowerCase();
      norm.full_day = ['true', '1', 'ja', 'yes', 'y', 'wahr'].includes(s);
    } else if (typeof norm.full_day !== 'boolean') {
      norm.full_day = true;
    }

    if (typeof (norm as any)._skip === 'string') {
      const s = (norm as any)._skip.trim().toLowerCase();
      (norm as any)._skip = ['true', '1', 'ja', 'yes', 'y', 'wahr', 't'].includes(s);
    }

    return norm;
  }

  function normalizeType(v: any): string {
    const s = String(v || '').trim().toLowerCase();
    if (['holiday', 'urlaub', 'u', 'vacation'].includes(s)) return 'holiday';
    if (['sick', 'krank', 'kr', 'au'].includes(s)) return 'sick';
    if (['overtime', 'ueberstundenabbau', 'überstundenabbau', 'freizeitausgleich', 'abbau'].includes(s)) return 'overtime';
    return s || 'holiday';
  }

  function normalizeStatus(v: any): string {
    const s = String(v || '').trim().toLowerCase();
    if (['approved', 'genehmigt', 'ok'].includes(s)) return 'approved';
    if (['requested', 'angefragt'].includes(s)) return 'requested';
    if (['rejected', 'abgelehnt'].includes(s)) return 'rejected';
    if (['cancelled', 'storniert'].includes(s)) return 'cancelled';
    return 'approved';
  }

  function toISODate(val: any): string {
    if (val == null || val === '') return '';
    if (typeof val === 'number' && Number.isFinite(val) && val > 20000 && val < 60000) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const ms = Math.round(val * 24 * 60 * 60 * 1000);
      const d = new Date(excelEpoch.getTime() + ms);
      return d.toISOString().slice(0, 10);
    }
    if (val instanceof Date && !isNaN(val.getTime())) {
      return val.toISOString().slice(0, 10);
    }
    const s = String(val).trim();
    const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m1) return s;
    const m2 = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m2) {
      const [_, dd, mm, yyyy] = m2 as any;
      const d = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
      return d;
    }
    const t = Date.parse(s);
    if (!isNaN(t)) return new Date(t).toISOString().slice(0, 10);
    return s;
  }

  const renderPreviewTable = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 13 }}>
      <thead>
        <tr style={{ background: colors.bgMuted }}>
          <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Zeile</th>
          <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Aktion</th>
          <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Mitarbeiter</th>
          <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Typ</th>
          <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Zeitraum</th>
          <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Fehler</th>
        </tr>
      </thead>
      <tbody>
        {preview.length === 0 && (
          <tr>
            <td colSpan={6} style={{ padding: 12, color: colors.textMuted, fontSize: 13 }}>Keine Vorschau vorhanden.</td>
          </tr>
        )}
        {preview.map((r, idx) => {
          const action = String(r.action || '').toLowerCase();
          const isCreateOrUpdate = action === 'create' || action === 'update';
          const isSkip = action === 'skip';
          const isError = action === 'error';
          const isUpdate = action === 'update';
          const rowStyle: React.CSSProperties = isCreateOrUpdate
            ? { background: colors.successLight, color: '#065f46' }
            : isSkip
              ? { background: colors.bgMuted, color: colors.textBody }
              : isError
                ? { background: colors.dangerLight, color: colors.danger }
                : {};
          const emp = (r as any).employee_name || (r as any).employee_email || (r as any).datev_personalnr || (r as any).employee || (r as any).employee_id || '—';
          const typ = (r as any).absence_type || (r as any).type || '—';
          const sd = (r as any).start_datetime || (r as any).from;
          const ed = (r as any).end_datetime || (r as any).to;
          const periodStr = sd && ed ? `${sd} – ${ed}` : '—';
          const titleSkip = isSkip ? 'fehlende Pflichtfelder' : undefined;
          return (
            <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}`, ...rowStyle }} title={titleSkip}>
              <td style={{ padding: '6px' }}>{idx + 1}</td>
              <td style={{ padding: '6px', textTransform: 'uppercase', display:'flex', alignItems:'center', gap:6, fontWeight: 500, fontSize: 12 }}>
                {r.action || '—'}
                {isUpdate && (
                  <span style={{ padding: '0 6px', borderRadius: 8, background: '#dbeafe', border: '1px solid #93c5fd', color: '#1e40af', fontSize: 10, fontWeight: 600 }}>UPDATE</span>
                )}
              </td>
              <td style={{ padding: '6px' }}>{emp}</td>
              <td style={{ padding: '6px' }}>{typ}</td>
              <td style={{ padding: '6px' }}>{periodStr}</td>
              <td style={{ padding: '6px' }}>{r.error || ''}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const hasErrors = (summary?.errors ?? preview.filter(r => String(r.action || '').toLowerCase() === 'error').length) > 0;
  const canCommit = preview.length > 0 && !hasErrors;

  function downloadErrorsCsv() {
    const errs = preview
      .map((r, i) => ({ index: i + 1, ...r }))
      .filter(r => String((r as any).action || '').toLowerCase() === 'error');
    if (errs.length === 0) return;
    const headers = ['line', 'action', 'employee', 'employee_name', 'absence_type', 'start_datetime', 'end_datetime', 'error'];
    const esc = (v: any) => {
      const s = String(v ?? '');
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const rowsCsv = errs.map((r: any) => [r.index, r.action, r.employee, r.employee_name, r.absence_type, r.start_datetime, r.end_datetime, r.error].map(esc).join(','));
    const csv = [headers.join(','), ...rowsCsv].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'absences_import_errors.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const hasPreview = preview.length > 0;

  return (
    <div role="dialog" aria-modal="true" style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.navy }}>Absences importieren</h3>
          <button
            onClick={onClose}
            aria-label="Schliessen"
            style={{
              border: 'none', background: 'transparent',
              fontSize: 18, cursor: 'pointer', color: colors.textLight,
              width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.color = colors.textBody; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = colors.textLight; }}
          >{'×'}</button>
        </div>

        {/* File Upload */}
        <div style={{ ...sectionStyle, marginBottom: 12 }}>
          <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 10 }}>XLSX oder CSV Datei hochladen</div>
          <input id="abs-import-file" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview([]); setSummary(null); setError(''); }} style={{ display:'none' }} />
          <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
            <button onClick={() => document.querySelector<HTMLInputElement>('#abs-import-file')?.click()} style={btnSecondary}>Datei {'wählen'}</button>
            <span style={{ fontSize: 13, color: file ? colors.textBody : colors.textLight, flex: 1 }}>{file ? file.name : 'Keine Datei'}</span>
            {file && !hasPreview && (
              <button
                onClick={handlePreview}
                disabled={busy}
                style={{ ...btnPrimary, opacity: busy ? 0.5 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}
              >
                {busy ? 'Wird geprüft...' : 'Vorschau'}
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div role="alert" style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 12,
            background: colors.dangerLight, color: colors.danger,
            fontSize: 12, fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        {/* Preview Table - only after dry run */}
        {hasPreview && (
          <>
            {summary && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12 }}>
                {typeof summary.creates === 'number' && <span style={{ color: colors.success, fontWeight: 500 }}>{summary.creates} neu</span>}
                {typeof summary.updates === 'number' && <span style={{ color: colors.slate, fontWeight: 500 }}>{summary.updates} update</span>}
                {typeof summary.skips === 'number' && summary.skips > 0 && <span style={{ color: colors.textMuted }}>{summary.skips} übersprungen</span>}
                {summary.errors > 0 && <span style={{ color: colors.danger, fontWeight: 500 }}>{summary.errors} Fehler</span>}
              </div>
            )}
            {renderPreviewTable()}
          </>
        )}

        {/* Action Buttons - context-dependent */}
        <div style={{ display: 'flex', gap: 8, marginTop: hasPreview ? 16 : 0 }}>
          {hasPreview && canCommit && (
            <button onClick={handleCommit} disabled={busy} style={{ ...btnPrimary, flex: 1, padding: '10px 0', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Importiere...' : 'Importieren'}
            </button>
          )}
          {hasPreview && hasErrors && (
            <button onClick={downloadErrorsCsv} style={{ ...btnSecondary, flex: 1, padding: '10px 0' }}>
              Fehler als CSV
            </button>
          )}
          {hasPreview && (
            <button onClick={() => { setPreview([]); setSummary(null); }} style={{ ...btnSecondary, flex: 1, padding: '10px 0' }}>
              Neue Datei
            </button>
          )}
          {!hasPreview && (
            <button onClick={onClose} disabled={busy} style={{ ...btnSecondary, flex: 1, padding: '10px 0' }}>
              {'Schließen'}
            </button>
          )}
        </div>

        {/* Erweitert: Template + Merge */}
        <div style={{ marginTop: 16, borderTop: `1px solid ${colors.border}`, paddingTop: 12 }}>
          <button
            onClick={() => setShowAdvanced(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: colors.textMuted, padding: 0 }}
          >
            {showAdvanced ? 'Erweitert ▾' : 'Erweitert ▸'}
          </button>
          {showAdvanced && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Merge Mode */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ ...labelTextStyle, fontSize: 12 }}>Merge</span>
                <select value={mergeMode} onChange={(e)=> setMergeMode(e.target.value as any)} style={selectStyle}>
                  <option value="auto">auto</option>
                  <option value="replace">replace</option>
                  <option value="none">none</option>
                </select>
              </div>
              {/* Template Download */}
              <div style={{ ...sectionStyle }}>
                <div style={sectionHeaderStyle}>Template herunterladen</div>
                <div style={{ display:'flex', gap: 8, flexWrap:'wrap', alignItems:'center', marginBottom: 10 }}>
                  <label style={labelStyle}>
                    <span style={labelTextStyle}>Prefill</span>
                    <select value={tplPrefill} onChange={(e)=> setTplPrefill(e.target.value as any)} style={selectStyle}>
                      <option value="none">Leer</option>
                      <option value="all">Alle MA</option>
                      <option value="active">Aktive</option>
                    </select>
                  </label>
                  <label style={labelStyle}>
                    <span style={labelTextStyle}>Format</span>
                    <select value={tplFormat} onChange={(e)=> setTplFormat(e.target.value as any)} style={selectStyle}>
                      <option value="xlsx">XLSX</option>
                      <option value="csv">CSV</option>
                    </select>
                  </label>
                  <label style={labelStyle}>
                    <input type="checkbox" checked={tplExamples} onChange={(e)=> setTplExamples(e.target.checked)} style={{ accentColor: colors.navy }} />
                    <span style={{ color: colors.textBody }}>Beispielzeilen</span>
                  </label>
                </div>
                <button onClick={handleDownloadTemplate} style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }}>Herunterladen</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
